import { AfterContentInit, ChangeDetectionStrategy, ChangeDetectorRef, Component, ElementRef, HostBinding, HostListener, Inject, Injector } from '@angular/core';
import { DomSanitizer, SafeStyle } from '@angular/platform-browser';
import { Observable } from 'rxjs';
import { distinctUntilChanged, tap } from 'rxjs/operators';
import { Guard } from 'src/core-lib/misselaineous/guards';
import { ComponentOverlay, Overlay } from './models/overlay.interface';
import { OverlayService } from './services/overlay.service';
import { OVERLAY_CONTEXT } from './tokens';

@Component({
    selector: 'overlay',
    templateUrl: './overlay.component.html',
    styleUrls: ['./overlay.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class OverlayComponent implements AfterContentInit {
    @HostBinding('style.') style?: SafeStyle;
    @HostBinding('class.overlay-bg') overlayBg: boolean = false;
    @HostBinding('class') classes: string;
    @HostBinding('class.pointer-events') overlayPointerEvents: boolean = false;

    private _stopScrolling: boolean = false;
    private _overlay: any;
    private _clientY: any;

    overlay$: Observable<any>;
    overlayClosing$: Observable<Overlay<any>>;
    overlayInjector: Injector;

    constructor(
        private host: ElementRef,
        private overlayService: OverlayService,
        private detector: ChangeDetectorRef,
        private injector: Injector,
        private sanitizer: DomSanitizer,
    ) {
        this.overlayClosing$ = this.overlayService.overlay$;

        this.style = this.sanitizer.bypassSecurityTrustStyle(` display: none; width: 0; --backdrop: #00000066;`);
    }

    ngAfterContentInit(): void {
        this.overlay$ = this.overlayService
            .overlay$
            .pipe(
                tap(overlay => this.makeInjector(overlay)),
                distinctUntilChanged((x, y) => {
                    if (x == null || y == null)
                        return false;
                    if (x.template != null && y.template != null)
                        return x.template.elementRef.nativeElement !== y.template.elementRef.nativeElement;
                    if (x.component != null && y.component != null)
                        return x.component != y.component;
                    return true;
                }),
                tap(overlay => this.show(overlay))
            );
    }

    @HostListener('click', ['$event.target'])
    onClick(target: ElementRef) {
        if (target === this.host.nativeElement) {
            this.overlayService.close();
        }
    }

    @HostListener('touchstart', ['$event'])
    start(event: TouchEvent) {
        this._clientY = event['targetTouches'][0].clientY;
    }

    @HostListener('touchmove', ['$event'])
    canScroll(event: TouchEvent) {
        if (this._stopScrolling && event.targetTouches[0].clientY < this._clientY) {
            event.preventDefault();
        }
    }

    stopScrolling() {
        this._stopScrolling = true;
    }

    startScrolling() {
        this._stopScrolling = false;
    }

    disableRubberBand(event, element: HTMLElement) {
        const clientY = event.targetTouches[0].clientY - this._clientY;

        if (element.scrollTop === 0 && clientY > 0) {
            // element is at the top of its scroll
            event.preventDefault();
        }

        if (this.isOverlayTotallyScrolled(element) && clientY < 0) {
            //element is at the top of its scroll
            event.preventDefault();
        }
    }

    private isOverlayTotallyScrolled(_overlay: HTMLElement) {
        // https://developer.mozilla.org/en-US/docs/Web/API/Element/scrollHeight#Problems_and_solutions
        return _overlay.scrollHeight - _overlay.scrollTop <= _overlay.clientHeight;
    }

    private show(overlay: Overlay<any> | ComponentOverlay<any>) {
        this._stopScrolling = false;
        const show = Guard.not.nullOrUndefined(overlay);
        const display = show ? 'block' : 'none';
        this.overlayBg = show && !overlay?.hideBackdrop;
        this.overlayPointerEvents = show && !overlay?.hidePointerEvents;
        this.classes = overlay?.classes;
        const backdropColor = overlay?.backdropColor != null ? overlay.backdropColor : '#00000066';
        const width = show ? '100vw' : '0';
        this.style = this.sanitizer.bypassSecurityTrustStyle(` display: ${display}; width: ${width}; --backdrop: ${backdropColor};`);
        this.detector.markForCheck();
    }

    private makeInjector(overlay: Overlay<any>): void {
        if (Guard.not.nullOrUndefined(overlay) && Guard.not.nullOrUndefined(overlay.component)) {
            this.overlayInjector = Injector.create({
                providers: [{
                    provide: OVERLAY_CONTEXT,
                    useValue: overlay.context,
                    deps: []
                }],
                parent: this.injector
            });
            this.detector.markForCheck();
        }
    }
}
